import { useEffect, useRef, useState } from 'react';
import { fetcher } from 'services';
import { EXAONE_MAX_TEXT_LENGTH, ExaOneMessage, ExaOneRateLimit, ExaOneRole } from 'services/fetcher/exaoneChatbot';

const useExaone = () => {
  /** 화면 대화 내역 (전송 시 최근 5개 메시지만 사용) */
  const [messages, setMessages] = useState<ExaOneMessage[]>([]);
  /** 진행 중인 답변의 사고 과정 (스트리밍 중에만 유효, 완료되면 비워짐) */
  const [reasoning, setReasoning] = useState<string>('');
  /** 진행 중인 답변 텍스트 (스트리밍 중에만 유효, 완료되면 messages로 이동) */
  const [answer, setAnswer] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [rateLimit, setRateLimit] = useState<ExaOneRateLimit>();
  const abortRef = useRef<(() => void) | null>(null);
  const requestIdRef = useRef(0);

  /** 언마운트 시 진행 중인 스트림 중단 */
  useEffect(() => () => { requestIdRef.current += 1; abortRef.current?.(); }, []);

  /**
   * EXAONE 챗봇 질문 전송 (스트리밍으로 수신)
   */
  const send = (content: string, image?: string) => {
    if (isLoading) return false;
    if (content.length > EXAONE_MAX_TEXT_LENGTH) {
      setErrorMessage(`질문은 ${EXAONE_MAX_TEXT_LENGTH.toLocaleString()}자 이하로 입력해주세요.`);
      return false;
    }
    if (!content.trim()) return false;

    const requestId = ++requestIdRef.current;
    const nextMessages: ExaOneMessage[] = [...messages, { role: 'user', content }];

    setMessages(nextMessages);
    setIsLoading(true);
    setErrorMessage('');
    setReasoning('');
    setAnswer('');

    let accumulatedReasoning = '';
    let accumulatedContent = '';
    // API가 응답 role을 내려주면 그걸 쓰고, 혹시 안 내려주면 'assistant'로 fallback
    let receivedRole: ExaOneRole | undefined;

    abortRef.current = fetcher.exaoneChatbot.streamChat(
      { messages: nextMessages, image },
      {
        onReasoning: (text, role) => {
          if (requestId !== requestIdRef.current) return;
          accumulatedReasoning += text;
          setReasoning(accumulatedReasoning);
          if (role) {
            receivedRole = role as ExaOneRole;
          }
        },
        onContent: (text, role) => {
          if (requestId !== requestIdRef.current) return;
          accumulatedContent += text;
          setAnswer(accumulatedContent);
          if (role) {
            receivedRole = role as ExaOneRole;
          }
        },
        onRateLimit: value => { if (requestId === requestIdRef.current) setRateLimit(value); },
        onDone: () => {
          if (requestId !== requestIdRef.current) return;
          requestIdRef.current += 1;
          // 서버는 빈 content를 거부하므로 빈 답변은 히스토리에 넣지 않는다.
          if (accumulatedContent.trim()) {
            setMessages([...nextMessages, { role: receivedRole ?? 'assistant', content: accumulatedContent }]);
          } else {
            setMessages(current => current === nextMessages ? messages : current);
            setErrorMessage('EXAONE 연동에 오류가 발생하였습니다.');
          }
          setAnswer('');
          setReasoning('');
          setIsLoading(false);
          abortRef.current = null;
        },
        onError: (_resultCode, resultMessage) => {
          if (requestId !== requestIdRef.current) return;
          requestIdRef.current += 1;
          // 거절된 질문을 이후 요청의 문맥에 반복해서 포함하지 않는다.
          setMessages(current => current === nextMessages ? messages : current);
          setErrorMessage(resultMessage || 'EXAONE 연동에 오류가 발생하였습니다.');
          setAnswer('');
          setReasoning('');
          setIsLoading(false);
          abortRef.current = null;
        },
      }
    );
    return true;
  };

  /** 응답 생성 중단 */
  const stop = () => {
    requestIdRef.current += 1;
    abortRef.current?.();
    abortRef.current = null;
    setIsLoading(false);
  };

  const clear = () => {
    stop();
    setMessages([]);
    setErrorMessage('');
    setAnswer('');
    setReasoning('');
  };

  return {
    send,
    stop,
    clear,
    messages,
    answer,
    reasoning,
    isLoading,
    errorMessage,
    rateLimit,
  };
};

export default useExaone;
